import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { financeApi, type ProfitLossStatement, type ProfitLossSection } from '@/api/financeApi';
import { formatINR } from '@/utils/format';

type PresetType = 'THIS_MONTH' | 'THIS_QUARTER' | 'THIS_FY' | 'CUSTOM';
type ViewMode = 'SUMMARY' | 'MONTHLY';
type CompareMode = 'NONE' | 'PREVIOUS_PERIOD' | 'PREVIOUS_YEAR';

export default function ProfitLossPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const today = new Date().toISOString().split('T')[0];
  const currentYear = new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1;

  const [fromDate, setFromDate] = useState(`${currentYear}-04-01`);
  const [toDate, setToDate] = useState(today);
  const [preset, setPreset] = useState<PresetType>('THIS_FY');
  const [viewMode, setViewMode] = useState<ViewMode>('SUMMARY');
  const [compareWith, setCompareWith] = useState<CompareMode>('NONE');

  const [loading, setLoading] = useState(true);
  const [pnl, setPnl] = useState<ProfitLossStatement | null>(null);

  // Section Collapse State
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    directIncome: false,
    directExpenses: false,
    indirectIncome: false,
    indirectExpenses: false,
  });

  const toggleSection = (key: string) => {
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const applyPreset = (type: PresetType) => {
    setPreset(type);
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    if (type === 'THIS_MONTH') {
      const firstDay = new Date(year, month, 1).toISOString().split('T')[0];
      const lastDay = new Date(year, month + 1, 0).toISOString().split('T')[0];
      setFromDate(firstDay);
      setToDate(lastDay);
    } else if (type === 'THIS_QUARTER') {
      const qStartMonth = Math.floor(month / 3) * 3;
      const firstDay = new Date(year, qStartMonth, 1).toISOString().split('T')[0];
      const lastDay = new Date(year, qStartMonth + 3, 0).toISOString().split('T')[0];
      setFromDate(firstDay);
      setToDate(lastDay);
    } else if (type === 'THIS_FY') {
      const fyStart = month >= 3 ? year : year - 1;
      setFromDate(`${fyStart}-04-01`);
      setToDate(now.toISOString().split('T')[0]);
    }
  };

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      const data = await financeApi.getProfitLoss({
        fromDate,
        toDate,
        compare: compareWith,
        view: viewMode,
      });
      setPnl(data);
    } catch (err: any) {
      console.error('Failed to load P&L:', err);
      toast.error(err.response?.data?.message || 'Failed to load Profit & Loss statement');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, compareWith, viewMode, toast]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    if (!pnl) return;
    const rows: (string | number)[][] = [
      ['DIRECT INCOME', ''],
      ...pnl.directIncome.items.map((i) => [`  ${i.code} - ${i.name}`, i.amount, i.comparisonAmount ?? '', i.changePercent ? `${i.changePercent}%` : '']),
      ['Total Direct Income', pnl.directIncome.total, pnl.directIncome.comparisonTotal ?? '', pnl.directIncome.changePercent ? `${pnl.directIncome.changePercent}%` : ''],
      ['', ''],
      ['DIRECT EXPENSES (COGS)', ''],
      ...pnl.directExpenses.items.map((i) => [`  ${i.code} - ${i.name}`, i.amount, i.comparisonAmount ?? '', i.changePercent ? `${i.changePercent}%` : '']),
      ['Total Direct Expenses', pnl.directExpenses.total, pnl.directExpenses.comparisonTotal ?? '', pnl.directExpenses.changePercent ? `${pnl.directExpenses.changePercent}%` : ''],
      ['', ''],
      ['GROSS PROFIT', pnl.grossProfit, pnl.summary?.comparisonGrossProfit ?? '', pnl.summary?.grossProfitChangePercent ? `${pnl.summary.grossProfitChangePercent}%` : ''],
      ['', ''],
      ['INDIRECT INCOME', ''],
      ...pnl.indirectIncome.items.map((i) => [`  ${i.code} - ${i.name}`, i.amount, i.comparisonAmount ?? '', i.changePercent ? `${i.changePercent}%` : '']),
      ['Total Indirect Income', pnl.indirectIncome.total, pnl.indirectIncome.comparisonTotal ?? '', pnl.indirectIncome.changePercent ? `${pnl.indirectIncome.changePercent}%` : ''],
      ['', ''],
      ['INDIRECT EXPENSES', ''],
      ...pnl.indirectExpenses.items.map((i) => [`  ${i.code} - ${i.name}`, i.amount, i.comparisonAmount ?? '', i.changePercent ? `${i.changePercent}%` : '']),
      ['Total Indirect Expenses', pnl.indirectExpenses.total, pnl.indirectExpenses.comparisonTotal ?? '', pnl.indirectExpenses.changePercent ? `${pnl.indirectExpenses.changePercent}%` : ''],
      ['', ''],
      ['NET PROFIT / (LOSS)', pnl.netProfit, pnl.summary?.comparisonNetProfit ?? '', pnl.summary?.netProfitChangePercent ? `${pnl.summary.netProfitChangePercent}%` : ''],
    ];

    const csv = [
      ['Particulars', 'Amount (INR)', 'Comparison Amount (INR)', 'Change %'].join(','),
      ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Profit_Loss_${fromDate}_to_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('P&L report exported');
  };

  const renderChangeBadge = (changePercent?: number | null) => {
    if (changePercent === undefined || changePercent === null) return null;
    const isPositive = changePercent >= 0;
    return (
      <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${isPositive ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
        <i className={isPositive ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} />
        {Math.abs(changePercent).toFixed(1)}%
      </span>
    );
  };

  const renderSectionHeader = (title: string, sectionKey: string, sectionData: ProfitLossSection, bgClass: string, textClass: string) => {
    const isCollapsed = collapsedSections[sectionKey];
    return (
      <div
        onClick={() => toggleSection(sectionKey)}
        className={`w-full flex items-center justify-between py-2.5 px-3.5 rounded-lg border ${bgClass} cursor-pointer hover:opacity-90 transition-opacity select-none`}
      >
        <div className="flex items-center gap-2">
          <i className={`ri-arrow-${isCollapsed ? 'right' : 'down'}-s-line text-sm text-slate-500`} />
          <h3 className={`text-xs font-bold uppercase tracking-wider ${textClass}`}>{title}</h3>
          <span className="text-[10px] font-medium text-slate-500 bg-white/70 px-1.5 py-0.5 rounded">
            {sectionData.items.length} accounts
          </span>
        </div>
        <div className="flex items-center gap-4 text-xs font-bold font-mono">
          <span>{formatINR(sectionData.total)}</span>
          {compareWith !== 'NONE' && (
            <span className="text-slate-500 font-normal">
              {formatINR(sectionData.comparisonTotal || 0)}
            </span>
          )}
          {renderChangeBadge(sectionData.changePercent)}
        </div>
      </div>
    );
  };

  return (
    <AppLayout>
      <div className="w-full flex flex-col min-h-screen bg-[#f8fafc] animate-fade-in">
        {/* Full Width Container */}
        <div className="w-full p-6 space-y-5">
          
          {/* Top Page Header */}
          <div className="w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-3 print:hidden">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-[#1e293b]">Profit & Loss Statement (P&L)</h1>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                  Income Statement
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                Financial performance statement summarizing revenue, cost of goods sold, overheads, and net profit.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handlePrint}
                className="h-9 px-3.5 rounded-lg bg-white hover:bg-slate-50 border border-[#e2e8f0] text-sm font-medium text-slate-700 transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <i className="ri-printer-line text-sm text-slate-500" />
                <span>Print</span>
              </button>
              <button
                onClick={handleExport}
                className="h-9 px-3.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <i className="ri-download-2-line text-sm text-white" />
                <span>Export CSV</span>
              </button>
              <button
                onClick={() => navigate('/finance/trial-balance')}
                className="h-9 px-3.5 rounded-lg bg-[#4f46e5] hover:bg-indigo-700 text-white text-sm font-medium transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <i className="ri-scales-3-line text-sm text-white" />
                <span>Trial Balance</span>
              </button>
            </div>
          </div>

          {/* Compact Control & Filter Bar */}
          <div className="w-full bg-white border border-[#e2e8f0] rounded-xl px-4 py-3 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 print:hidden">
            {/* Left: Date Picker & Presets */}
            <div className="flex items-center gap-2.5 flex-wrap">
              <div className="flex items-center gap-1.5 bg-white border border-[#e2e8f0] rounded-lg px-2.5 h-9 text-sm text-slate-700">
                <span className="text-slate-400 font-medium">Period:</span>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setPreset('CUSTOM');
                  }}
                  className="bg-transparent font-medium text-[#1e293b] focus:outline-none cursor-pointer"
                />
                <span className="text-slate-400 font-medium">to</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setPreset('CUSTOM');
                  }}
                  className="bg-transparent font-medium text-[#1e293b] focus:outline-none cursor-pointer"
                />
              </div>

              {/* Presets */}
              <div className="flex items-center gap-0.5 bg-slate-100 p-0.5 rounded-lg border border-[#e2e8f0] text-xs">
                {(
                  [
                    { id: 'THIS_MONTH', label: 'This Month' },
                    { id: 'THIS_QUARTER', label: 'This Quarter' },
                    { id: 'THIS_FY', label: 'This FY' },
                  ] as const
                ).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => applyPreset(p.id)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ${
                      preset === p.id
                        ? 'bg-[#4f46e5] text-white shadow-sm font-semibold'
                        : 'bg-transparent text-slate-600 hover:bg-slate-200/60'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Right: Compare & View Mode */}
            <div className="flex items-center gap-2.5 flex-wrap">
              <div className="flex items-center gap-1.5 bg-white border border-[#e2e8f0] rounded-lg px-2.5 h-9 text-sm">
                <span className="text-slate-400 font-medium">Compare:</span>
                <select
                  value={compareWith}
                  onChange={(e) => setCompareWith(e.target.value as CompareMode)}
                  className="bg-transparent font-medium text-slate-800 focus:outline-none cursor-pointer"
                >
                  <option value="NONE">None</option>
                  <option value="PREVIOUS_PERIOD">Previous Period</option>
                  <option value="PREVIOUS_YEAR">Previous Year</option>
                </select>
              </div>

              <div className="flex items-center gap-0.5 bg-slate-100 p-0.5 rounded-lg border border-[#e2e8f0] text-xs">
                <button
                  onClick={() => setViewMode('SUMMARY')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ${
                    viewMode === 'SUMMARY'
                      ? 'bg-slate-900 text-white shadow-sm font-semibold'
                      : 'bg-transparent text-slate-600 hover:bg-slate-200/60'
                  }`}
                >
                  Summary
                </button>
                <button
                  onClick={() => setViewMode('MONTHLY')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ${
                    viewMode === 'MONTHLY'
                      ? 'bg-slate-900 text-white shadow-sm font-semibold'
                      : 'bg-transparent text-slate-600 hover:bg-slate-200/60'
                  }`}
                >
                  Monthly View
                </button>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          {pnl && (
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5 print:hidden">
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Direct Income</p>
                <p className="text-xl font-bold font-mono text-blue-700 mt-1">{formatINR(pnl.summary.totalIncome)}</p>
                {renderChangeBadge(pnl.summary.totalIncomeChangePercent)}
              </div>

              <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Total Expenses</p>
                <p className="text-xl font-bold font-mono text-amber-700 mt-1">{formatINR(pnl.summary.totalExpenses)}</p>
                {renderChangeBadge(pnl.summary.totalExpensesChangePercent)}
              </div>

              <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Gross Profit</p>
                <p className={`text-xl font-bold font-mono mt-1 ${pnl.summary.grossProfit >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                  {formatINR(pnl.summary.grossProfit)}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5 font-mono">{pnl.summary.grossMarginPercent}% margin</p>
              </div>

              <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Net Profit / (Loss)</p>
                <p className={`text-xl font-bold font-mono mt-1 ${pnl.summary.isProfitable ? 'text-emerald-700' : 'text-rose-600'}`}>
                  {formatINR(pnl.summary.netProfit)}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5 font-mono">{pnl.summary.netMarginPercent}% margin</p>
              </div>

              <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm flex flex-col justify-between">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Net Margin Status</p>
                <div className="mt-1">
                  <span
                    className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      pnl.summary.netMarginPercent >= 20
                        ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                        : pnl.summary.netMarginPercent >= 10
                        ? 'bg-amber-50 text-amber-800 border border-amber-200'
                        : 'bg-rose-50 text-rose-800 border border-rose-200'
                    }`}
                  >
                    {pnl.summary.netMarginPercent >= 20 ? 'Optimal' : pnl.summary.netMarginPercent >= 10 ? 'Moderate' : 'Low Profitability'}
                  </span>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Carried to Equity on Balance Sheet</p>
              </div>
            </div>
          )}

          {/* Main Statement Content */}
          <div className="w-full">
            {loading ? (
              <div className="w-full bg-white border border-[#e2e8f0] rounded-xl py-16 text-center text-slate-400 flex flex-col items-center justify-center gap-2 shadow-sm">
                <i className="ri-loader-4-line animate-spin text-2xl text-primary-600" />
                <p className="text-xs font-medium text-slate-600">Computing Profit & Loss Statement...</p>
              </div>
            ) : !pnl ? null : viewMode === 'MONTHLY' ? (
              /* MONTHLY BREAKDOWN TABLE */
              <div className="w-full bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm p-4 space-y-3">
                <div className="flex items-center justify-between pb-3 border-b border-[#e2e8f0]">
                  <h3 className="text-sm font-bold text-[#1e293b]">Month-Wise Profitability Breakdown</h3>
                  <span className="text-xs font-mono text-slate-500">Period: {pnl.period.fromDate} to {pnl.period.toDate}</span>
                </div>
                {pnl.monthlyData && pnl.monthlyData.length > 0 ? (
                  <div className="w-full overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50 text-slate-400 text-[10px] tracking-wider uppercase font-semibold">
                          <th className="py-2.5 px-3">Month</th>
                          <th className="py-2.5 px-3 text-right">Total Income</th>
                          <th className="py-2.5 px-3 text-right">Total Expenses</th>
                          <th className="py-2.5 px-3 text-right">Net Profit</th>
                          <th className="py-2.5 px-3 text-right">Net Margin %</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-mono">
                        {pnl.monthlyData.map((m) => (
                          <tr key={m.month} className="hover:bg-slate-50/70 transition-colors">
                            <td className="py-2.5 px-3 font-sans font-semibold text-slate-800">{m.label}</td>
                            <td className="py-2.5 px-3 text-right text-emerald-700 font-semibold">{formatINR(m.income)}</td>
                            <td className="py-2.5 px-3 text-right text-amber-700 font-semibold">{formatINR(m.expenses)}</td>
                            <td className={`py-2.5 px-3 text-right font-bold ${m.netProfit >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                              {formatINR(m.netProfit)}
                            </td>
                            <td className="py-2.5 px-3 text-right font-sans text-slate-700">{m.netMarginPercent}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 py-6 text-center italic">No monthly transactions recorded in this period</p>
                )}
              </div>
            ) : (
              /* SUMMARY P&L STATEMENT VIEW */
              <div className="w-full bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm p-5 space-y-5">
                
                {/* 1. DIRECT INCOME */}
                <div className="w-full space-y-2">
                  {renderSectionHeader(
                    '1. Operating Revenue & Direct Income',
                    'directIncome',
                    pnl.directIncome,
                    'bg-blue-50/70 border-blue-200/80',
                    'text-blue-900'
                  )}
                  {!collapsedSections.directIncome && (
                    <div className="w-full space-y-1 pl-2">
                      {pnl.directIncome.items.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => navigate(`/finance/ledger?accountId=${item.id}`)}
                          className="w-full flex items-center justify-between py-1.5 px-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors text-xs group"
                        >
                          <span className="font-medium text-slate-800 group-hover:text-primary-600 transition-colors">
                            {item.code} — {item.name}
                          </span>
                          <div className="flex items-center gap-4 font-mono">
                            <span className="font-bold text-[#1e293b]">{formatINR(item.amount)}</span>
                            {compareWith !== 'NONE' && (
                              <span className="text-slate-500 font-normal">
                                {formatINR(item.comparisonAmount || 0)}
                              </span>
                            )}
                            {renderChangeBadge(item.changePercent)}
                          </div>
                        </div>
                      ))}
                      {pnl.directIncome.items.length === 0 && (
                        <p className="text-slate-400 italic text-xs py-1 px-3">No direct income recorded</p>
                      )}
                    </div>
                  )}
                </div>

                {/* 2. DIRECT EXPENSES */}
                <div className="w-full space-y-2">
                  {renderSectionHeader(
                    '2. Cost of Goods Sold & Direct Expenses',
                    'directExpenses',
                    pnl.directExpenses,
                    'bg-amber-50/70 border-amber-200/80',
                    'text-amber-900'
                  )}
                  {!collapsedSections.directExpenses && (
                    <div className="w-full space-y-1 pl-2">
                      {pnl.directExpenses.items.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => navigate(`/finance/ledger?accountId=${item.id}`)}
                          className="w-full flex items-center justify-between py-1.5 px-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors text-xs group"
                        >
                          <span className="font-medium text-slate-800 group-hover:text-primary-600 transition-colors">
                            {item.code} — {item.name}
                          </span>
                          <div className="flex items-center gap-4 font-mono">
                            <span className="font-bold text-[#1e293b]">{formatINR(item.amount)}</span>
                            {compareWith !== 'NONE' && (
                              <span className="text-slate-500 font-normal">
                                {formatINR(item.comparisonAmount || 0)}
                              </span>
                            )}
                            {renderChangeBadge(item.changePercent)}
                          </div>
                        </div>
                      ))}
                      {pnl.directExpenses.items.length === 0 && (
                        <p className="text-slate-400 italic text-xs py-1 px-3">No direct expenses recorded</p>
                      )}
                    </div>
                  )}
                </div>

                {/* GROSS PROFIT CALLOUT */}
                <div className="w-full bg-slate-100/90 border border-[#e2e8f0] rounded-lg p-3.5 flex items-center justify-between font-bold text-xs">
                  <span className="text-[#1e293b] uppercase tracking-wider">GROSS PROFIT</span>
                  <div className="flex items-center gap-4 font-mono">
                    <span className={`text-sm font-bold ${pnl.grossProfit >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                      {formatINR(pnl.grossProfit)}
                    </span>
                    {compareWith !== 'NONE' && pnl.summary?.comparisonGrossProfit !== undefined && (
                      <span className="text-slate-500 font-normal">
                        {formatINR(pnl.summary.comparisonGrossProfit)}
                      </span>
                    )}
                    {renderChangeBadge(pnl.summary?.grossProfitChangePercent)}
                  </div>
                </div>

                {/* 3. INDIRECT INCOME */}
                {pnl.indirectIncome.items.length > 0 && (
                  <div className="w-full space-y-2">
                    {renderSectionHeader(
                      '3. Indirect & Other Income',
                      'indirectIncome',
                      pnl.indirectIncome,
                      'bg-teal-50/70 border-teal-200/80',
                      'text-teal-900'
                    )}
                    {!collapsedSections.indirectIncome && (
                      <div className="w-full space-y-1 pl-2">
                        {pnl.indirectIncome.items.map((item) => (
                          <div
                            key={item.id}
                            onClick={() => navigate(`/finance/ledger?accountId=${item.id}`)}
                            className="w-full flex items-center justify-between py-1.5 px-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors text-xs group"
                          >
                            <span className="font-medium text-slate-800 group-hover:text-primary-600 transition-colors">
                              {item.code} — {item.name}
                            </span>
                            <div className="flex items-center gap-4 font-mono">
                              <span className="font-bold text-[#1e293b]">{formatINR(item.amount)}</span>
                              {compareWith !== 'NONE' && (
                                <span className="text-slate-500 font-normal">
                                  {formatINR(item.comparisonAmount || 0)}
                                </span>
                              )}
                              {renderChangeBadge(item.changePercent)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* 4. INDIRECT EXPENSES */}
                <div className="w-full space-y-2">
                  {renderSectionHeader(
                    '4. Indirect Expenses & Administrative Overheads',
                    'indirectExpenses',
                    pnl.indirectExpenses,
                    'bg-purple-50/70 border-purple-200/80',
                    'text-purple-900'
                  )}
                  {!collapsedSections.indirectExpenses && (
                    <div className="w-full space-y-1 pl-2">
                      {pnl.indirectExpenses.items.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => navigate(`/finance/ledger?accountId=${item.id}`)}
                          className="w-full flex items-center justify-between py-1.5 px-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors text-xs group"
                        >
                          <span className="font-medium text-slate-800 group-hover:text-primary-600 transition-colors">
                            {item.code} — {item.name}
                          </span>
                          <div className="flex items-center gap-4 font-mono">
                            <span className="font-bold text-[#1e293b]">{formatINR(item.amount)}</span>
                            {compareWith !== 'NONE' && (
                              <span className="text-slate-500 font-normal">
                                {formatINR(item.comparisonAmount || 0)}
                              </span>
                            )}
                            {renderChangeBadge(item.changePercent)}
                          </div>
                        </div>
                      ))}
                      {pnl.indirectExpenses.items.length === 0 && (
                        <p className="text-slate-400 italic text-xs py-1 px-3">No indirect expenses recorded</p>
                      )}
                    </div>
                  )}
                </div>

                {/* FINAL NET PROFIT CALLOUT BAR */}
                <div className="w-full bg-primary-50 border-2 border-primary-200 rounded-xl p-4 flex items-center justify-between font-bold">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-primary-800 font-bold">NET PROFIT / (LOSS)</p>
                    <p className="text-[11px] text-primary-600 font-normal mt-0.5">Transferred dynamically to Balance Sheet (Equity)</p>
                  </div>
                  <div className="flex items-center gap-4 font-mono">
                    <span className={`text-xl ${pnl.netProfit >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                      {formatINR(pnl.netProfit)}
                    </span>
                    {compareWith !== 'NONE' && pnl.summary?.comparisonNetProfit !== undefined && (
                      <span className="text-slate-500 text-xs font-normal">
                        {formatINR(pnl.summary.comparisonNetProfit)}
                      </span>
                    )}
                    {renderChangeBadge(pnl.summary?.netProfitChangePercent)}
                  </div>
                </div>

              </div>
            )}
          </div>

        </div>
      </div>
    </AppLayout>
  );
}
